// src/pages/Dashboard/DashboardPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Typography, Paper, Grid, Card, Box,
  Avatar, Stack, Divider, Button, Tooltip,
  LinearProgress, Chip, Alert, Skeleton,
  List, ListItem, ListItemText, ListItemAvatar, ListItemIcon,
} from '@mui/material';
import {
  AssignmentOutlined, RocketLaunchOutlined, CreditCardOutlined,
  BusinessOutlined, ArrowForward, Lightbulb, Analytics, Speed,
  WorkspacePremium, Upgrade, Verified, CheckCircle, CalendarToday,
  EventNote, Visibility, ContactMail, LocalOffer
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchProfile } from '../../store/slices/authSlice';
import { usePremium } from '../../hooks/usePremium';
import api from '../../services/api';

const P = {
  forestGreen: '#2E7D32', deepForest: '#1B5E20', mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A', brandLime: '#CDDC39', charcoal: '#1A2A3A',
  softGreen: 'rgba(46,125,50,0.06)', white: '#FFFFFF', offWhite: '#F8F9FA',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg,#2E7D32 0%,#4CAF50 40%,#8BC34A 70%,#CDDC39 100%)',
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

const formatUserType = (type) => {
  if (!type) return 'User';
  if (type === 'organization') return 'Organization';
  if (type === 'supplier') return 'Vendor';
  return type.charAt(0).toUpperCase() + type.slice(1);
};

// ── Subscription Detail Card ────────────────────────────────────────────────────
const SubscriptionDetailCard = ({ subInfo }) => {
  if (!subInfo) return null;

  const planName = subInfo.plan_name || 'Premium';
  const startDate = fmt(subInfo.current_period_start);
  const expiryDate = fmt(subInfo.current_period_end);
  const isCancelling = subInfo.cancel_at_period_end;

  return (
    <Card sx={{
      p: 3, borderRadius: '20px', border: `1px solid ${P.border}`,
      background: `linear-gradient(135deg, ${P.softGreen} 0%, ${P.white} 100%)`,
      boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: P.forestGreen, color: 'white', width: 56, height: 56 }}>
            <WorkspacePremium />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: P.charcoal }}>
              {planName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Premium Subscription
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={3} alignItems="center">
          {startDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ color: P.forestGreen, fontSize: 18 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Subscribed on</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: P.charcoal }}>{startDate}</Typography>
              </Box>
            </Box>
          )}
          {expiryDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventNote sx={{ color: P.forestGreen }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {isCancelling ? 'Cancels on' : 'Renews on'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: P.forestGreen }}>
                  {expiryDate}
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

// ── Stat Card ──────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon, color, subtitle, isLoading, size = 'small' }) => (
  <Card sx={{
    p: size === 'large' ? 4 : 3, borderRadius: '20px', border: `1px solid ${P.border}`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)', transition: 'transform .3s',
    '&:hover': { transform: 'translateY(-5px)', borderColor: color },
    height: '100%',
  }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar sx={{ bgcolor: `${color}18`, color, width: size === 'large' ? 64 : 56, height: size === 'large' ? 64 : 56 }}>
        {icon}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>{title}</Typography>
        {isLoading
          ? <Skeleton width={80} height={40} />
          : <Typography variant={size === 'large' ? 'h3' : 'h4'} sx={{ fontWeight: 800, color: P.charcoal }}>{value}</Typography>}
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  </Card>
);

// ── Usage Bar ──────────────────────────────────────────────────────────────────
const UsageBar = ({ label, used, total, color }) => {
  const unlimited = !total;
  const pct = unlimited ? 100 : Math.min((used / total) * 100, 100);
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color }}>
          {unlimited ? '∞ Unlimited' : `${used} / ${total}`}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct}
        sx={{ height: 6, borderRadius: 3, bgcolor: '#E5E7EB',
          '& .MuiLinearProgress-bar': { bgcolor: color } }} />
    </Box>
  );
};

// ── Safe fetch helper ─────────────────────────────────────────────────────────
const safeFetch = async (fn) => {
  try { return await fn(); } catch (e) { console.warn('safeFetch:', e); return null; }
};

// ── Main Dashboard Component ──────────────────────────────────────────────────
const DashboardPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector(s => s.auth);
  const { isPremium, planType, dmUsed, dmLimit, bidsUsed, bidsLimit, refresh: refreshPremium } = usePremium();

  // One-time profile refresh if premium but subscription_info missing
  const hasRefreshed = useRef(false);
  useEffect(() => {
    if (!hasRefreshed.current && (isPremium || user?.is_premium) && !user?.subscription_info && isAuthenticated) {
      hasRefreshed.current = true;
      dispatch(fetchProfile()).then(() => refreshPremium());
    }
  }, [isPremium, user, isAuthenticated, dispatch, refreshPremium]);

  const hasPremium = !!user?.subscription_info || isPremium || user?.is_premium === true;
  const isOrganization = user?.user_type === 'organization';
  const isSupplier = user?.user_type === 'supplier';
  const accountTypeDisplay = formatUserType(user?.user_type);
  const subInfo = user?.subscription_info || null;

  // Dashboard stats from backend
  const [dashboardStats, setDashboardStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  const [recentProblems, setRecentProblems] = useState([]);
  const [recentSolutions, setRecentSolutions] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // ── Stats + Dashboard fetch ──────────────────────────────────────────────
  // We wait for BOTH access_token in localStorage AND user?.user_type in Redux.
  // These can arrive in either order depending on the auth service implementation,
  // so we poll every 100ms (max 5s) until both are present before fetching.
  useEffect(() => {
    if (!user?.user_type) return;   // Redux not ready yet — skip

    let cancelled = false;

    const waitForTokenThenFetch = async () => {
      // Poll until access_token lands in localStorage (max 50 × 100ms = 5s)
      for (let i = 0; i < 50; i++) {
        if (cancelled) return;
        const token = localStorage.getItem('access_token');
        if (token) break;
        await new Promise(r => setTimeout(r, 100));
      }

      if (cancelled) return;

      if (!localStorage.getItem('access_token')) {
        // Timed out — stop spinners
        setStatsLoading(false);
        setLoading(false);
        return;
      }

      // Token confirmed present — now safe to fetch
      setStatsLoading(true);
      try {
        const stats = await safeFetch(() => api.get('/problems/dashboard-stats/'));
        if (!cancelled) setDashboardStats(stats);
      } catch (err) {
        console.warn('Failed to fetch dashboard stats:', err);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }

      if (!cancelled) fetchDashboard();
    };

    waitForTokenThenFetch();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_type]);

  const fetchDashboard = async () => {
    // Read user_type fresh from localStorage rather than relying on the
    // closed-over isOrganization / isSupplier consts, which may be stale
    // when this function is defined during the render that first sets user.
    const storedUser   = JSON.parse(localStorage.getItem('user') || 'null');
    const userType     = storedUser?.user_type ?? '';
    const isOrg        = userType === 'organization';
    const isVendor     = userType === 'supplier' || userType === 'vendor';

    // Hard stop if no token — nothing below will succeed without it.
    if (!localStorage.getItem('access_token')) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setFetchError(null);

      // ── Recent Problems ──────────────────────────────────────────────────
      let problems = [];
      if (isOrg) {
        const my = await safeFetch(() =>
          api.problems.list({ page: 1, page_size: 3 })
        );
        problems = my?.results ?? (Array.isArray(my) ? my : []);
        if (!problems.length) {
          const all = await safeFetch(() =>
            api.problems.list({ page: 1, page_size: 3, ordering: '-created_at' })
          );
          problems = all?.results ?? (Array.isArray(all) ? all : []);
        }
      } else {
        const all = await safeFetch(() =>
          api.problems.list({ page: 1, page_size: 3, ordering: '-created_at' })
        );
        problems = all?.results ?? (Array.isArray(all) ? all : []);
      }
      setRecentProblems(problems.slice(0, 3));

      // ── Recent Solutions (suppliers only) ───────────────────────────────
      let solutions = [];
      if (isVendor) {
        const orgsData = await safeFetch(() => api.get('/organizations/my-organizations/'));
        const orgs = Array.isArray(orgsData) ? orgsData : (orgsData?.results ?? []);
        if (orgs.length > 0) {
          const mySols = await safeFetch(() =>
            api.solutions.list({ provider: orgs[0].id, page: 1, page_size: 3 })
          );
          solutions = mySols?.results ?? (Array.isArray(mySols) ? mySols : []);
        }
      }
      setRecentSolutions(solutions.slice(0, 3));

    } catch (err) {
      setFetchError('Some dashboard data could not be loaded.');
    } finally {
      setLoading(false);
    }

    if (hasPremium) fetchAIInsights();
  };

  const fetchAIInsights = async () => {
    setInsightsLoading(true);
    try {
      const mod = await import('../../services/ai').catch(() => null);
      if (!mod?.default?.getMarketInsights) return;
      const insights = await Promise.race([
        mod.default.getMarketInsights(),
        new Promise((_, r) => setTimeout(() => r(new Error('timeout')), 10000)),
      ]);
      setAiInsights(insights);
    } catch { /* non-fatal */ }
    finally { setInsightsLoading(false); }
  };

  const usageColor = (used, limit) => {
    if (!limit) return '#10B981';
    const r = used / limit;
    return r >= 1 ? '#EF4444' : r >= 0.6 ? '#F59E0B' : P.forestGreen;
  };

  const currentPlanValue = hasPremium ? (subInfo?.plan_name || planType || 'Premium') : 'Free';
  const currentPlanSubtitle = hasPremium && subInfo?.current_period_end
    ? (subInfo.cancel_at_period_end ? `Cancels ${fmt(subInfo.current_period_end)}` : `Renews ${fmt(subInfo.current_period_end)}`)
    : hasPremium ? 'Active' : 'Upgrade to unlock all features';
  const planIconColor = hasPremium ? '#10B981' : P.charcoal;

  // Get stat card values based on user type using backend stats
  const getStatCards = () => {
    if (isOrganization) {
      const problemsCount = dashboardStats?.problems_count ?? 0;
      const solutionTags = dashboardStats?.solution_tags ?? { unique_suppliers: 0, total_tags: 0, problems_with_tags: 0 };
      const tagsSubtitle = solutionTags.unique_suppliers === 0 
        ? 'No tags yet' 
        : `${solutionTags.unique_suppliers} suppliers tagged your ${solutionTags.problems_with_tags} challenges ${solutionTags.total_tags} times`;
      
      return [
        {
          title: 'My Problems',
          value: statsLoading ? '—' : problemsCount,
          icon: <AssignmentOutlined />,
          color: P.forestGreen,
          subtitle: problemsCount === 0 ? 'Post your first challenge' : `${problemsCount} active challenges`,
          isLoading: statsLoading,
          size: 'large'
        },
        {
          title: 'Solution Tags',
          value: statsLoading ? '—' : solutionTags.total_tags,
          icon: <LocalOffer />,
          color: P.mediumGreen,
          subtitle: tagsSubtitle,
          isLoading: statsLoading,
          size: 'large'
        },
        {
          title: 'Current Plan',
          value: currentPlanValue,
          icon: hasPremium ? <WorkspacePremium /> : <CreditCardOutlined />,
          color: planIconColor,
          subtitle: currentPlanSubtitle,
          isLoading: false,
          size: 'small'
        },
        {
          title: 'Account Type',
          value: accountTypeDisplay,
          icon: <BusinessOutlined />,
          color: P.forestGreen,
          subtitle: 'Post problems & find solutions',
          isLoading: false,
          size: 'small'
        }
      ];
    } else if (isSupplier) {
      const solutionsCount = dashboardStats?.solutions_count ?? 0;
      const totalViews = dashboardStats?.total_views ?? 0;
      const inquiries = dashboardStats?.inquiries ?? { total_inquiries: 0, unique_organizations: 0 };
      
      return [
        {
          title: 'My Solutions',
          value: statsLoading ? '—' : solutionsCount,
          icon: <RocketLaunchOutlined />,
          color: P.forestGreen,
          subtitle: solutionsCount === 0 ? 'List your first solution' : `${solutionsCount} active solutions`,
          isLoading: statsLoading,
          size: 'large'
        },
        {
          title: 'Solution Views',
          value: statsLoading ? '—' : totalViews,
          icon: <Visibility />,
          color: P.mediumGreen,
          subtitle: totalViews === 0 ? 'No views yet' : `Total solution views`,
          isLoading: statsLoading,
          size: 'large'
        },
        {
          title: 'Contacts',
          value: statsLoading ? '—' : inquiries.total_inquiries,
          icon: <ContactMail />,
          color: P.lightGreen,
          subtitle: inquiries.total_inquiries === 0 ? 'No inquiries yet' : `${inquiries.total_inquiries} organization inquiries`,
          isLoading: statsLoading,
          size: 'large'
        },
        {
          title: 'Current Plan',
          value: currentPlanValue,
          icon: hasPremium ? <WorkspacePremium /> : <CreditCardOutlined />,
          color: planIconColor,
          subtitle: currentPlanSubtitle,
          isLoading: false,
          size: 'small'
        }
      ];
    }
    // Fallback for admin or unknown
    return [];
  };

  const statCards = getStatCards();

  return (
    <Box sx={{ bgcolor: P.offWhite, minHeight: '100vh', py: 6 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 900, color: P.charcoal, mb: 1 }}>Dashboard</Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              Welcome back,{' '}
              <Box component="span" sx={{ color: P.forestGreen, fontWeight: 700 }}>
                {user?.full_name || user?.first_name || user?.email || 'Innovator'}
              </Box>!
            </Typography>
          </Box>
          <Tooltip title={hasPremium ? 'Manage your Premium plan' : 'Upgrade to Premium'}>
            <Button
              variant="contained"
              startIcon={hasPremium ? <Verified /> : <Upgrade />}
              onClick={() => navigate('/pricing')}
              sx={{
                background: hasPremium ? '#10B981' : P.gradient,
                borderRadius: '12px', px: 3, textTransform: 'none', fontWeight: 700,
              }}
            >
              {hasPremium ? '✦ Premium Active' : 'Upgrade to Premium'}
            </Button>
          </Tooltip>
        </Box>

        {fetchError && <Alert severity="warning" sx={{ mb: 4, borderRadius: '12px' }} onClose={() => setFetchError(null)}>{fetchError}</Alert>}

        {/* Stat Cards - Dynamic based on user type */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {statCards.map((card, index) => {
            let mdCols = 3;
            if (isOrganization) {
              mdCols = index < 2 ? 6 : 3;
            } else if (isSupplier) {
              mdCols = index < 3 ? 4 : 3;
            }
            return (
              <Grid item xs={12} sm={6} md={mdCols} key={card.title}>
                <StatCard {...card} />
              </Grid>
            );
          })}
        </Grid>

        {/* Subscription Detail Card */}
        {hasPremium && subInfo && (
          <Box sx={{ mb: 4 }}>
            <SubscriptionDetailCard subInfo={subInfo} />
          </Box>
        )}

        {/* Detailed Premium Card */}
        {hasPremium && subInfo && (
          <Paper sx={{ p: 4, borderRadius: '24px', border: `2px solid ${P.forestGreen}`, background: P.gradient, color: 'white', mb: 6 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <WorkspacePremium sx={{ fontSize: 22 }} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{subInfo.plan_name}</Typography>
                </Stack>
                <Chip label={`${subInfo.interval === 'year' ? 'Annual' : 'Monthly'} · ${subInfo.currency} ${subInfo.amount}/${subInfo.interval}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
              </Box>
              <Chip label="Active ✓" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700 }} />
            </Stack>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.25)', mb: 2 }} />
            {subInfo.features?.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, opacity: 0.9 }}>Your plan includes:</Typography>
                <List dense disablePadding>
                  {subInfo.features.map((f, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.3 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}><CheckCircle sx={{ fontSize: 17, color: P.brandLime }} /></ListItemIcon>
                      <ListItemText primary={f} primaryTypographyProps={{ fontSize: '.88rem', color: 'rgba(255,255,255,0.93)' }} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            <Button fullWidth variant="contained" onClick={() => window.location.href = '/pricing'} sx={{ mt: 2.5, bgcolor: 'white', color: P.forestGreen, fontWeight: 700, borderRadius: '10px', textTransform: 'none' }}>Manage Subscription →</Button>
          </Paper>
        )}

        {/* Usage & AI Insights */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, borderRadius: '24px', border: `1px solid ${P.border}`, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Speed sx={{ color: P.forestGreen }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Usage & Limits</Typography>
                <Chip label={hasPremium ? '✦ Premium' : 'Free Tier'} size="small" sx={{ ml: 'auto', bgcolor: hasPremium ? 'rgba(16,185,129,.1)' : '#FEF3C7', color: hasPremium ? '#10B981' : '#F59E0B', fontWeight: 700 }} />
              </Box>
              <Divider sx={{ mb: 3 }} />
              <UsageBar label="Direct Messages / month" used={dmUsed} total={hasPremium ? null : dmLimit} color={usageColor(dmUsed, hasPremium ? null : dmLimit)} />
              {isSupplier && <UsageBar label="Bids Submitted / month" used={bidsUsed} total={hasPremium ? null : bidsLimit} color={usageColor(bidsUsed, hasPremium ? null : bidsLimit)} />}
              {isOrganization && <UsageBar label="Tagged Solutions per Problem" used={hasPremium ? 0 : 5} total={hasPremium ? null : 5} color={hasPremium ? '#10B981' : P.forestGreen} />}
              {!hasPremium && <Button fullWidth variant="outlined" startIcon={<Upgrade />} onClick={() => navigate('/pricing')} sx={{ mt: 3, borderColor: P.forestGreen, color: P.forestGreen, borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>Upgrade to Unlimited</Button>}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, borderRadius: '24px', border: `1px solid ${P.border}`, height: '100%', background: hasPremium ? P.gradient : P.white, color: hasPremium ? 'white' : 'inherit' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Analytics sx={{ color: hasPremium ? 'white' : P.forestGreen }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>AI Market Insights</Typography>
                {!hasPremium && <Chip label="Premium" size="small" sx={{ ml: 'auto', background: P.gradient, color: 'white', fontWeight: 600 }} />}
              </Box>
              <Divider sx={{ mb: 3, borderColor: hasPremium ? 'rgba(255,255,255,0.25)' : undefined }} />
              {!hasPremium ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Lightbulb sx={{ fontSize: 48, color: P.brandLime, mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 2 }}>Unlock AI-powered market insights</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>Real-time trends, price predictions, and opportunity alerts</Typography>
                  <Button variant="contained" startIcon={<Upgrade />} onClick={() => navigate('/pricing')} sx={{ background: P.gradient, textTransform: 'none', fontWeight: 600 }}>Upgrade to Premium</Button>
                </Box>
              ) : insightsLoading ? (
                <Stack spacing={2}><Skeleton variant="text" height={60} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} /><Skeleton variant="text" height={60} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} /></Stack>
              ) : aiInsights ? (
                <>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>{aiInsights.period} Market Summary</Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}><Typography variant="caption" sx={{ opacity: 0.75 }}>New Problems</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{aiInsights.market_growth?.problems?.new || 0}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" sx={{ opacity: 0.75 }}>New Solutions</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{aiInsights.market_growth?.solutions?.new || 0}</Typography></Grid>
                  </Grid>
                  {aiInsights.trending_categories?.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Trending</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {aiInsights.trending_categories.slice(0, 4).map((c, i) => <Chip key={i} label={c.name} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />)}
                      </Stack>
                    </>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}><Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>AI insights temporarily unavailable.</Typography><Button variant="outlined" size="small" onClick={fetchAIInsights} sx={{ color: 'white', borderColor: 'rgba(255,255,255,.5)', textTransform: 'none' }}>Retry</Button></Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Problems & Solutions */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, borderRadius: '24px', border: `1px solid ${P.border}`, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{isOrganization ? 'My Problems' : 'Recent Problems'}</Typography>
                <Button endIcon={<ArrowForward />} onClick={() => navigate('/problems')} sx={{ color: P.forestGreen, textTransform: 'none' }}>{isOrganization ? 'View all' : 'See all'}</Button>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              {loading ? <Stack spacing={2}>{[1,2,3].map(i => <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 2 }} />)}</Stack>
                : recentProblems.length > 0 ? (
                  <List disablePadding>
                    {recentProblems.map(p => (
                      <ListItem button key={p.id} onClick={() => navigate(`/problems/${p.id}`)} sx={{ borderRadius: 2, mb: 1, '&:hover': { bgcolor: P.softGreen } }}>
                        <ListItemAvatar><Avatar sx={{ bgcolor: P.softGreen, color: P.forestGreen }}><AssignmentOutlined /></Avatar></ListItemAvatar>
                        <ListItemText primary={p.title} secondary={`${p.organization_name || 'Your org'} · ${new Date(p.created_at).toLocaleDateString()}`} primaryTypographyProps={{ fontWeight: 600 }} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Avatar sx={{ bgcolor: P.softGreen, m: '0 auto 16px', width: 64, height: 64 }}><AssignmentOutlined sx={{ color: P.forestGreen }} /></Avatar>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>{isOrganization ? "You haven't posted any problems yet" : 'No problems yet'}</Typography>
                    <Button variant="outlined" onClick={() => navigate('/problems/create')} sx={{ borderRadius: '10px', color: P.forestGreen, borderColor: P.forestGreen, textTransform: 'none' }}>Post a Problem</Button>
                  </Box>
                )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, borderRadius: '24px', border: `1px solid ${P.border}`, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{isSupplier ? 'My Solutions' : 'Recent Solutions'}</Typography>
                <Button endIcon={<ArrowForward />} onClick={() => navigate('/solutions')} sx={{ color: P.forestGreen, textTransform: 'none' }}>Browse All</Button>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              {loading ? <Stack spacing={2}>{[1,2,3].map(i => <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 2 }} />)}</Stack>
                : recentSolutions.length > 0 ? (
                  <List disablePadding>
                    {recentSolutions.map(s => (
                      <ListItem button key={s.id} onClick={() => navigate(`/solutions/${s.id}`)} sx={{ borderRadius: 2, mb: 1, '&:hover': { bgcolor: P.softGreen } }}>
                        <ListItemAvatar><Avatar sx={{ bgcolor: P.softGreen, color: P.mediumGreen }}><RocketLaunchOutlined /></Avatar></ListItemAvatar>
                        <ListItemText primary={s.title} secondary={`${s.provider_name} · Rating: ${s.rating_avg || 'New'}`} primaryTypographyProps={{ fontWeight: 600 }} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Avatar sx={{ bgcolor: P.softGreen, m: '0 auto 16px', width: 64, height: 64 }}><RocketLaunchOutlined sx={{ color: P.mediumGreen }} /></Avatar>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                      {isSupplier ? 'No solutions listed yet' : 'Find solutions for your challenges'}
                    </Typography>
                    {isSupplier
                      ? <Button variant="outlined" onClick={() => navigate('/solutions/create')} sx={{ borderRadius: '10px', color: P.forestGreen, borderColor: P.forestGreen, textTransform: 'none' }}>List a Solution</Button>
                      : <Button variant="outlined" onClick={() => navigate('/solutions')} sx={{ borderRadius: '10px', color: P.forestGreen, borderColor: P.forestGreen, textTransform: 'none' }}>Browse Solutions</Button>
                    }
                  </Box>
                )}
            </Paper>
          </Grid>
        </Grid>

        {/* Upgrade Banner for Free Users */}
        {!hasPremium && (
          <Paper sx={{ mt: 4, p: 4, borderRadius: '24px', background: P.gradient, color: 'white' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={3}>
              <Box><Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Unlock Unlimited Potential</Typography><Typography variant="body2" sx={{ opacity: 0.9 }}>Upgrade to Premium for unlimited access, AI insights, and all platform features</Typography></Box>
              <Button variant="contained" size="large" startIcon={<Upgrade />} onClick={() => navigate('/pricing')} sx={{ bgcolor: 'white', color: P.forestGreen, borderRadius: '12px', px: 4, fontWeight: 700, flexShrink: 0, textTransform: 'none', '&:hover': { bgcolor: '#F5F5F5' } }}>Upgrade Now</Button>
            </Stack>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default DashboardPage;